import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * Modal per duplicare un torneo con opzioni personalizzabili
 */
@customElement('pm-duplicate-tournament-modal')
export class PmDuplicateTournamentModal extends LitElement {
    @property({ type: Number, attribute: 'tournament-id' }) tournamentId = 0;
    @property({ type: String, attribute: 'tournament-name' }) tournamentName = '';
    @property({ type: String, attribute: 'tournament-status' }) tournamentStatus = 'draft';
    
    @state() private _isOpen = false;
    @state() private _newName = '';
    @state() private _keepMatches = false;
    @state() private _isSubmitting = false;

    static styles = css`
        :host {
            display: none;
        }

        :host([open]) {
            display: block;
        }

        .modal-backdrop {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            z-index: 1050;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: fadeIn 0.2s ease-in-out;
        }

        @keyframes fadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }

        .modal-content {
            background: var(--bs-body-bg, #fff);
            border-radius: 8px;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
            max-width: 500px;
            width: 90%;
            max-height: 90vh;
            overflow: auto;
            animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
            from {
                transform: translateY(-50px);
                opacity: 0;
            }
            to {
                transform: translateY(0);
                opacity: 1;
            }
        }

        .modal-header {
            padding: 1rem 1.5rem;
            border-bottom: 1px solid var(--bs-border-color, #dee2e6);
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        .modal-header h5 {
            margin: 0;
            font-size: 1.25rem;
            font-weight: 500;
            color: var(--bs-body-color, #212529);
        }

        .close-btn {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: var(--bs-body-color, #212529);
            opacity: 0.5;
            transition: opacity 0.2s;
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .close-btn:hover {
            opacity: 1;
        }

        .modal-body {
            padding: 1.5rem;
        }

        .form-group {
            margin-bottom: 1.5rem;
        }

        .form-label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            color: var(--bs-body-color, #212529);
        }

        .form-control {
            width: 100%;
            padding: 0.5rem 0.75rem;
            font-size: 1rem;
            border: 1px solid var(--bs-border-color, #ced4da);
            border-radius: 0.375rem;
            background: var(--bs-body-bg, #fff);
            color: var(--bs-body-color, #212529);
            transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        }

        .form-control:focus {
            outline: none;
            border-color: #86b7fe;
            box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }

        .form-check {
            display: flex;
            align-items: flex-start;
            padding: 1rem;
            background: var(--bs-light, #f8f9fa);
            border-radius: 0.375rem;
            border: 1px solid var(--bs-border-color, #dee2e6);
        }

        .form-check-input {
            width: 1.25rem;
            height: 1.25rem;
            margin-right: 0.75rem;
            cursor: pointer;
            flex-shrink: 0;
        }

        .form-check-label {
            cursor: pointer;
            user-select: none;
        }

        .form-check-label strong {
            display: block;
            margin-bottom: 0.25rem;
            color: var(--bs-body-color, #212529);
        }

        .form-check-label small {
            display: block;
            color: var(--bs-secondary, #6c757d);
            line-height: 1.4;
        }

        .alert {
            padding: 0.75rem 1rem;
            margin-bottom: 1rem;
            border: 1px solid transparent;
            border-radius: 0.375rem;
        }

        .alert-warning {
            color: #664d03;
            background-color: #fff3cd;
            border-color: #ffecb5;
        }

        .alert-info {
            color: #055160;
            background-color: #cff4fc;
            border-color: #b6effb;
        }

        .modal-footer {
            padding: 1rem 1.5rem;
            border-top: 1px solid var(--bs-border-color, #dee2e6);
            display: flex;
            gap: 0.5rem;
            justify-content: flex-end;
        }

        .btn {
            padding: 0.5rem 1rem;
            font-size: 1rem;
            border: 1px solid transparent;
            border-radius: 0.375rem;
            cursor: pointer;
            transition: all 0.15s ease-in-out;
            font-weight: 400;
        }

        .btn:disabled {
            opacity: 0.65;
            cursor: not-allowed;
        }

        .btn-secondary {
            color: #fff;
            background-color: #6c757d;
            border-color: #6c757d;
        }

        .btn-secondary:hover:not(:disabled) {
            background-color: #5c636a;
            border-color: #565e64;
        }

        .btn-primary {
            color: #fff;
            background-color: #0d6efd;
            border-color: #0d6efd;
        }

        .btn-primary:hover:not(:disabled) {
            background-color: #0b5ed7;
            border-color: #0a58ca;
        }

        .spinner {
            display: inline-block;
            width: 1rem;
            height: 1rem;
            border: 2px solid currentColor;
            border-right-color: transparent;
            border-radius: 50%;
            animation: spin 0.75s linear infinite;
            margin-right: 0.5rem;
        }

        @keyframes spin {
            to {
                transform: rotate(360deg);
            }
        }
    `;

    connectedCallback() {
        super.connectedCallback();
        // Imposta il nome di default quando il componente viene connesso
        if (this.tournamentName && !this._newName) {
            this._newName = `${this.tournamentName} (Copia)`;
        }
    }

    open() {
        // Debug log per verificare che gli attributi siano impostati
        console.log('Opening duplicate modal:', {
            tournamentId: this.tournamentId,
            tournamentName: this.tournamentName,
            tournamentStatus: this.tournamentStatus
        });
        
        if (!this.tournamentId || this.tournamentId === 0) {
            console.error('Tournament ID is missing or invalid');
            alert('Errore: ID torneo non valido. Ricarica la pagina e riprova.');
            return;
        }
        
        this._isOpen = true;
        this._newName = `${this.tournamentName} (Copia)`;
        this._keepMatches = false;
        this._isSubmitting = false;
        this.setAttribute('open', '');
        
        // Previeni lo scroll del body
        document.body.style.overflow = 'hidden';
        
        // Forza il re-rendering del componente
        this.requestUpdate();
    }

    close() {
        this._isOpen = false;
        this.removeAttribute('open');
        
        // Ripristina lo scroll del body
        document.body.style.overflow = '';
    }

    private _handleBackdropClick(e: MouseEvent) {
        if (e.target === e.currentTarget) {
            this.close();
        }
    }

    private _handleNameInput(e: Event) {
        const input = e.target as HTMLInputElement;
        this._newName = input.value;
    }

    private _handleKeepMatchesChange(e: Event) {
        const checkbox = e.target as HTMLInputElement;
        this._keepMatches = checkbox.checked;
    }

    private _getCSRFToken(): string {
        // Cerca il token CSRF in diversi posti
        // 1. Cerca nel cookie
        const cookieMatch = document.cookie.match(/csrftoken=([^;]+)/);
        if (cookieMatch) {
            return cookieMatch[1];
        }
        
        // 2. Cerca nell'input hidden del form
        const tokenInput = document.querySelector('[name=csrfmiddlewaretoken]') as HTMLInputElement;
        if (tokenInput?.value) {
            return tokenInput.value;
        }
        
        // 3. Cerca nel meta tag
        const metaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
        if (metaTag?.content) {
            return metaTag.content;
        }
        
        console.error('CSRF token not found');
        return '';
    }

    private async _handleSubmit(e: Event) {
        e.preventDefault();
        
        if (!this._newName.trim()) {
            alert('Inserisci un nome per il torneo duplicato');
            return;
        }

        this._isSubmitting = true;
        this.requestUpdate();

        try {
            const csrfToken = this._getCSRFToken();
            
            if (!csrfToken) {
                alert('Errore: token CSRF non trovato. Ricarica la pagina e riprova.');
                this._isSubmitting = false;
                this.requestUpdate();
                return;
            }
            
            console.log('Duplicating tournament:', {
                id: this.tournamentId,
                newName: this._newName,
                keepMatches: this._keepMatches,
                url: `/simulations/tournaments/${this.tournamentId}/duplicate/`
            });
            
            const formData = new FormData();
            formData.append('new_name', this._newName);
            formData.append('keep_matches', this._keepMatches ? 'true' : 'false');
            
            const response = await fetch(`/simulations/tournaments/${this.tournamentId}/duplicate/`, {
                method: 'POST',
                headers: {
                    'X-CSRFToken': csrfToken,
                },
                body: formData,
            });

            if (response.ok) {
                console.log('Tournament duplicated successfully, redirecting...');
                // Redirect alla pagina restituita dal server
                window.location.href = response.url;
            } else {
                console.error('Duplication failed:', response.status, response.statusText);
                const text = await response.text();
                console.error('Error response:', text);
                alert(`Errore durante la duplicazione (${response.status}): ${response.statusText}\n\nDettagli: ${text.substring(0, 200)}`);
                this._isSubmitting = false;
                this.requestUpdate();
            }
        } catch (error) {
            console.error('Errore durante la duplicazione:', error);
            alert(`Errore durante la duplicazione del torneo: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`);
            this._isSubmitting = false;
            this.requestUpdate();
        }
    }

    render() {
        if (!this._isOpen) {
            return html``;
        }

        const showMatchWarning = this.tournamentStatus !== 'draft';

        return html`
            <div class="modal-backdrop" @click=${this._handleBackdropClick}>
                <div class="modal-content" @click=${(e: Event) => e.stopPropagation()}>
                    <div class="modal-header">
                        <h5>
                            <i class="fas fa-copy"></i>
                            Duplica Torneo
                        </h5>
                        <button class="close-btn" @click=${this.close} aria-label="Chiudi">
                            <span>&times;</span>
                        </button>
                    </div>

                    <form @submit=${this._handleSubmit}>
                        <div class="modal-body">
                            ${showMatchWarning ? html`
                                <div class="alert alert-info">
                                    <strong><i class="fas fa-info-circle"></i> Nota:</strong>
                                    Stai duplicando un torneo ${this.tournamentStatus === 'completed' ? 'completato' : 'in corso'}.
                                    Puoi scegliere se mantenere il bracket generato o partire da zero.
                                </div>
                            ` : ''}

                            <div class="form-group">
                                <label class="form-label" for="tournamentName">
                                    Nome del nuovo torneo
                                </label>
                                <input
                                    type="text"
                                    id="tournamentName"
                                    class="form-control"
                                    .value=${this._newName}
                                    @input=${this._handleNameInput}
                                    placeholder="Inserisci il nome del torneo..."
                                    required
                                    ?disabled=${this._isSubmitting}
                                />
                            </div>

                            <div class="form-group">
                                <div class="form-check">
                                    <input
                                        type="checkbox"
                                        id="keepMatches"
                                        class="form-check-input"
                                        .checked=${this._keepMatches}
                                        @change=${this._handleKeepMatchesChange}
                                        ?disabled=${this._isSubmitting || this.tournamentStatus === 'draft'}
                                    />
                                    <label class="form-check-label" for="keepMatches">
                                        <strong>Mantieni bracket e match generati</strong>
                                        <small>
                                            ${this.tournamentStatus === 'draft' 
                                                ? 'Non disponibile per tornei in bozza (nessun match generato)'
                                                : 'Se selezionato, il torneo duplicato includerà lo stesso bracket con match già organizzati. I punteggi saranno azzerati e il torneo sarà in modalità bozza.'}
                                        </small>
                                    </label>
                                </div>
                            </div>

                            ${this._keepMatches && showMatchWarning ? html`
                                <div class="alert alert-warning">
                                    <strong><i class="fas fa-exclamation-triangle"></i> Attenzione:</strong>
                                    Il bracket verrà copiato ma tutti i punteggi saranno azzerati.
                                    Il nuovo torneo sarà in modalità bozza e potrà essere modificato prima di essere avviato.
                                </div>
                            ` : ''}
                        </div>

                        <div class="modal-footer">
                            <button
                                type="button"
                                class="btn btn-secondary"
                                @click=${this.close}
                                ?disabled=${this._isSubmitting}
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                class="btn btn-primary"
                                ?disabled=${this._isSubmitting}
                            >
                                ${this._isSubmitting ? html`
                                    <span class="spinner"></span>
                                    Duplicazione...
                                ` : html`
                                    <i class="fas fa-copy"></i>
                                    Duplica
                                `}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'pm-duplicate-tournament-modal': PmDuplicateTournamentModal;
    }
}
